/**
 * Which sound effects a frame's sim events start (spec 07 §4), from where the C64 game asks for each
 * (docs/research/sfx.md §2). Pure. Call once per sim frame and pass each name to `MusicPlayer.effect`,
 * in order: the player applies the C64's own rules (in-game tunes only, jump first, one start per
 * main-loop pass).
 *
 * The C64's requests, traced (the index is the value written to $B0; the jump sets $B1):
 * - jump: D_222B `inc $B1` - a jump off the ground (joystick up, D_220C) or a bounce off a bubble
 *   (D_2162 -> D_222B). Sim: `jump`, `bounce`.
 * - pickup (1): D_29C0 $2A09, a killed monster's item; D_2CB7 $2D06, the level's food or power-up.
 *   Not the giant item (D_2ACB), the clear's bubble food (L0EC5), the potion stage's items (D_31A2)
 *   or the secret room's diamonds: those routines never write $B0.
 * - boltHit (2): $10D6 onwards (special-enemies.s, the "baron" labels are wrong), a bolt - a lightning
 *   bubble's or a red-cross shot, both run that code - killing a monster. Not the bolt setting off, not
 *   a hit on the level-100 boss ($1134 writes no $B0).
 * - extraLife (3): D_F1AC itself, when a score passes the next extra-life mark (not EXTEND's life).
 * - potion (4): the potions 16-18 (D_2F7D/D_2F83/D_2F89 $2F95).
 * - candyLamp (5): the candies 3-5 and the red and yellow lamps 9-10 (L_2DD2); not the purple lamp
 *   (11) or the green (28), which leave the pickup's request.
 * A power-up that leaves the round before the main loop runs again (the umbrellas 20 and 22, the doors
 * 32 and 33) or that starts its own tune (the heart, 24: subtune 4) is never heard: the level's
 * start clears the request (D_05C5).
 */

import type { ItemKind, SimEvent } from '../../sim/types';
import type { C64EffectName, EffectName, ExtraEffectName } from './effectNames';

/** What the events alone cannot say; the integrator knows it from the world. */
export interface EffectContext {
  /**
   * The kind of an item by id, as it was before the step took it (`collect` does not say): only a
   * monster's item and the level's food sound. Unknown ids sound (the safe default).
   */
  itemKind?: (itemId: number) => ItemKind | undefined;
  /**
   * A red-cross shot is in flight (`boltLooks(world)` has one with `redCross`): a kill the sim reports
   * as 'other' this frame is then the shot's, and sounds like a lightning bolt's (one power-up a round,
   * so no strike, blast or heart kill shares the round).
   */
  redCross?: boolean;
}

const CANDY_LAMP = new Set([3, 4, 5, 9, 10]);
const POTION = new Set([16, 17, 18]);
/** Heard never: gone from the round (umbrellas, doors) or under their own tune (the heart). */
const UNHEARD = new Set([20, 22, 24, 32, 33]);
const SOUNDING_ITEMS: ReadonlySet<ItemKind> = new Set<ItemKind>(['death', 'food', 'powerup']);
/** When several $B0 requests fall in one frame, the one kept: extra life is set last (D_F1AC). */
const C64_ORDER: readonly C64EffectName[] = ['extraLife', 'potion', 'candyLamp', 'boltHit', 'pickup'];
/** One extra a frame, the most telling. */
const EXTRA_ORDER: readonly ExtraEffectName[] = ['death', 'levelClear', 'hurryUp', 'chain', 'trap', 'pop', 'blow'];

function powerupEffect(item: number): C64EffectName | null {
  if (UNHEARD.has(item)) return null;
  if (POTION.has(item)) return 'potion';
  return CANDY_LAMP.has(item) ? 'candyLamp' : 'pickup';
}

function extraOf(e: SimEvent): ExtraEffectName | null {
  switch (e.type) {
    case 'blow': return 'blow';
    case 'pop': return 'pop';
    case 'chain': return e.kills >= 2 ? 'chain' : null;
    case 'trapped': return 'trap';
    case 'playerDied': return 'death';
    case 'levelClear': return 'levelClear';
    case 'hurryUp': return 'hurryUp';
    default: return null;
  }
}

/**
 * The effects the events of one sim frame start: at most the jump, one of the other five, and (with
 * `extras`) one extra - in that order.
 */
export function effectsFor(events: readonly SimEvent[], extras: boolean, context: EffectContext = {}): EffectName[] {
  let jump = false;
  const c64 = new Set<C64EffectName>();
  const extra = new Set<ExtraEffectName>();
  for (const e of events) {
    if (e.type === 'jump' || e.type === 'bounce') jump = true;
    else if (e.type === 'collect') {
      const kind = context.itemKind?.(e.itemId);
      if (kind === undefined || SOUNDING_ITEMS.has(kind)) c64.add('pickup');
    } else if (e.type === 'powerup') {
      const fx = powerupEffect(e.item);
      if (fx) c64.add(fx);
    } else if (e.type === 'extraLife') c64.add('extraLife');
    else if (e.type === 'monsterDied') {
      if (e.how === 'lightning' || (e.how === 'other' && context.redCross === true)) c64.add('boltHit');
    }
    if (extras) {
      const x = extraOf(e);
      if (x) extra.add(x);
    }
  }
  const out: EffectName[] = [];
  if (jump) out.push('jump');
  const one = C64_ORDER.find((n) => c64.has(n));
  if (one) out.push(one);
  const x = EXTRA_ORDER.find((n) => extra.has(n));
  if (x) out.push(x);
  return out;
}
